import { useParams } from 'react-router-dom';
import {
  Typography,
  Container,
  Box,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { appDetails } from '../utils/appDetails';

export default function AppDetailsPage() {
  const { slug } = useParams();
  const app = slug && appDetails[slug as keyof typeof appDetails];

  if (!app) {
    return (
      <Container>
        <Typography variant="h4" mt={8} textAlign="center" fontWeight={600}>
          App Not Found
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ fontFamily: `'Inter', 'Roboto', sans-serif` }}>
      {/* Section: Header */}
      <Box
        sx={{
          width: '100vw',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <img
              src={app.imageUrl}
              alt={app.title}
              style={{
                maxWidth: '240px',
                borderRadius: 16,
                boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
              }}
            />
            <Typography variant="h3" fontWeight={700} mt={4} gutterBottom>
              {app.title}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              {app.description}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Section: Image Gallery */}
      <Box sx={{ width: '100vw', bgcolor: '#f3f3f3', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Image Gallery
          </Typography>
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 3,
              py: 2,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {app.screenshots.map((src, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: 250,
                  height: 480,
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: 3,
                  backgroundColor: '#fff',
                  flexShrink: 0,
                }}
              >
                <img
                  src={src}
                  alt={`Screenshot ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Section: Description & Features */}
      <Box sx={{ width: '100vw', bgcolor: '#1e1e1e', py: 6 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            {/* Description */}
            <Box flex={1}>
              <Typography variant="h5" gutterBottom color='#fff'>
                Description
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1rem' }} color='#fff'>
                {app.detailedDescription}
              </Typography>
            </Box>

            {/* Vertical Divider */}
            <Box
              sx={{
                width: '1px',
                bgcolor: 'rgba(0,0,0,0.1)',
                alignSelf: 'stretch',
                display: { xs: 'none', md: 'block' },
              }}
            />

            {/* Features */}
            <Box flex={1}>
              <Typography variant="h5" gutterBottom color='#fff'>
                Features
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                {app.features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 'calc(50% - 8px)',
                    }}
                  >
                    <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2"color='#fff'>{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Section: Privacy Policy */}
      <Box sx={{ width: '100vw', bgcolor: '#e8f5e9', py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Privacy Policy
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              whiteSpace: 'pre-line',
              maxHeight: '400px',
              overflowY: 'auto',
              bgcolor: '#ffffff',
              borderRadius: 3,
              boxShadow: 2,
              lineHeight: 1.6,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.95rem',
                fontFamily: 'monospace',
              }}
            >
              {app.privacyPolicy}
            </Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
